import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * The single door into the delegation screen for VPs and managers.
 *
 * This used to render only while something was unassigned, which meant a fully
 * assigned book of business left a VP with no way to reach the screen at all —
 * no reassigning a territory to a new manager, no moving accounts mid-quarter.
 * It now always renders and only changes tone: amber when work is waiting,
 * neutral once everything is placed.
 */

const AlertIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const PeopleIcon = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const ChevronIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AssignAccountsBanner = ({ role, scopeUserId, unassignedCount = 0, assignedCount = null }) => {
  const navigate = useNavigate();
  if (role !== 'vp' && role !== 'manager') return null;

  const pending = unassignedCount > 0;
  const tone = pending
    ? { bg: '#fffbeb', border: '1px solid #fde68a', title: '#92400e', sub: '#d97706', icon: '#d97706' }
    : { bg: '#ffffff', border: '1px solid #c7d2fe', title: '#1e3a8a', sub: '#64748b', icon: '#64748b' };

  const title = role === 'vp' ? 'Assign Accounts to Management' : 'Assign Accounts to Reps';
  let subtitle;
  if (pending) {
    subtitle = `${unassignedCount} unassigned`;
  } else if (assignedCount != null) {
    subtitle = `All ${assignedCount} accounts assigned · tap to reassign`;
  } else {
    subtitle = 'All accounts assigned · tap to reassign';
  }

  return (
    <button
      onClick={() => navigate(`/field-intel/accounts?scopeTo=${scopeUserId}`)}
      style={{ ...styles.banner, backgroundColor: tone.bg, border: tone.border }}
    >
      <div style={styles.left}>
        {pending ? <AlertIcon color={tone.icon} /> : <PeopleIcon color={tone.icon} />}
        <div style={styles.text}>
          <span style={{ ...styles.title, color: tone.title }}>{title}</span>
          <span style={{ ...styles.subtitle, color: tone.sub }}>{subtitle}</span>
        </div>
      </div>
      <ChevronIcon color={tone.icon} />
    </button>
  );
};

const styles = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  text: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
  },
  subtitle: {
    fontSize: '12px',
    fontWeight: '500',
  },
};

export default AssignAccountsBanner;
